import { CARRY_MODES, GLYPH, GLYPH_IDS, GLYPH_SLOT_LEVELS, GLYPHS, HEROES, LOADOUTS, PALADIN_AURAS, SKILL_TREES, TOMES, glyphFits, glyphSlotsOpen, glyphTurns, type CarryMode, type ForkId, type GlyphId, type SkillDef, type SkillId } from '@somnhold/content';
import { carried, gearMods, glyphOwned, tomeSkill, heroStats, knownSkills, knownVariants, loadoutAt, skillAt, type CampaignCommand, type CampaignState, type Loadout, type PlayerState } from '@somnhold/sim';
import { audio } from '../audio/sfx.ts';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { FORK_ICON, SKILL_LOOK, lookOf } from './skill-tree.ts';
import { formPicker, uniquesOnButton } from './form-cards.ts';

type Cmd = (c: CampaignCommand) => Promise<boolean>;

// Kept across rerenders: the preparation and the slot being looked at.
let shown = 0;
let slot: 0 | 1 | 2 = 0;

const variantsOf = (cls: PlayerState['hero']['heroClass'], skill: SkillId): ForkId[] =>
  SKILL_TREES[cls].flatMap((n) => (n.effect.kind === 'variant' && n.effect.skill === skill ? [n.effect.fork] : []));

/**
 * "What to carry" (docs/PODERES.md §3): the hero's preparations as tabs, the three buttons as they look
 * in a fight, the skills a slot can hold and the ways to carry the chosen one side by side. Every
 * change saves at once and is free. `mode`, in a waiting room, makes that kind of fight use the
 * preparation picked.
 */
export const loadoutEditor = (s: CampaignState, p: PlayerState, cmd: Cmd, rerender: () => void, mode?: CarryMode): HTMLElement => {
  const hero = p.hero;
  const cls = hero.heroClass;
  const over = s.status !== 'active';
  const stored = hero.loadouts ?? [];
  const count = Math.max(1, stored.length);
  if (mode) shown = hero.carry?.[mode] ?? 0;
  if (shown >= count) shown = 0;
  const l = loadoutAt(hero, shown);
  const stats = heroStats({ pid: p.id, name: '', cls, level: hero.level, control: 'live', powerBp: 10000, post: 0, gear: gearMods(hero) });
  diffStats = stats;

  const save = async (next: Loadout, index = shown): Promise<boolean> => {
    // The first save starts from what the hero was carrying, so the others can come after it.
    if (!stored.length && index > 0 && !(await cmd({ t: 'loadout', index: 0, loadout: loadoutAt(hero, 0) }))) return false;
    const ok = await cmd({ t: 'loadout', index, loadout: next });
    if (ok) audio.play('click', 0.5);
    rerender();
    return ok;
  };
  const withPick = (k: 0 | 1 | 2, skill: SkillId, variant: ForkId | null): Loadout => {
    const picks = l.picks.map((x) => ({ ...x })) as Loadout['picks'];
    picks[k] = { skill, variant };
    // A glyph that doesn't fit the new skill comes off its button.
    const g = l.glyphs?.[k];
    if (l.glyphs && g && !glyphFits(skill, g)) return { ...l, picks, glyphs: withGlyph(k, null).glyphs };
    return { ...l, picks };
  };
  const withGlyph = (k: 0 | 1 | 2, g: GlyphId | null): Loadout => {
    const glyphs = [...(l.glyphs ?? [null, null, null])] as NonNullable<Loadout['glyphs']>;
    glyphs[k] = g;
    return { ...l, glyphs };
  };

  const wrap = h('div.loadout', { 'data-test': 'loadout' });
  // In a waiting room the card's own heading names it already.
  wrap.append(mode ? h('p.small.lo-free', {}, t('carry.free')) : h('div.sec-title', {}, h('h4', {}, t('carry.title')), h('span.small', {}, t('carry.free'))));

  // The preparations, as tabs; a new one starts as a copy of the one shown.
  const tabs = h('div.ptabs.lo-tabs', { role: 'tablist' });
  for (let i = 0; i < count; i++) {
    const name = stored[i]?.name || t('carry.prep', { n: i + 1 });
    tabs.append(
      h(
        `button.ptab${i === shown ? '.on' : ''}`,
        {
          role: 'tab',
          'aria-selected': String(i === shown),
          'data-prep': String(i),
          onclick: async () => {
            shown = i;
            if (mode && !over && (hero.carry?.[mode] ?? 0) !== i && (await cmd({ t: 'carry', mode, index: i }))) audio.play('click', 0.5);
            rerender();
          },
        },
        name,
      ),
    );
  }
  if (count < LOADOUTS.count && !over)
    tabs.append(
      h(
        'button.ptab.lo-new',
        {
          'data-act': 'new-prep',
          onclick: async () => {
            const at = stored.length ? stored.length : 1;
            if (await save({ ...l, name: '' }, at)) {
              shown = at;
              if (mode) await cmd({ t: 'carry', mode, index: at });
              rerender();
            }
          },
        },
        '+ ',
        t('carry.new'),
      ),
    );
  wrap.append(tabs);

  // Its name, and the kinds of fight that use it.
  const name = h('input.lo-name', { type: 'text', maxlength: String(LOADOUTS.nameMax), value: l.name ?? '', placeholder: t('carry.prep', { n: shown + 1 }), 'aria-label': t('carry.name'), disabled: over });
  name.addEventListener('change', () => void save({ ...l, name: name.value.trim() }));
  wrap.append(
    name,
    h(
      'div.lo-uses',
      {},
      h('span.small', {}, t('carry.usedIn')),
      ...CARRY_MODES.map((m) => {
        const on = (hero.carry?.[m] ?? 0) === shown;
        return h(
          `button.lo-mode${on ? '.on' : ''}`,
          {
            'data-mode': m,
            'aria-pressed': String(on),
            disabled: over || on,
            onclick: async () => {
              if (stored.length || shown === 0 ? await cmd({ t: 'carry', mode: m, index: shown }) : false) audio.play('click', 0.5);
              rerender();
            },
          },
          t(`carry.mode.${m}`),
        );
      }),
    ),
  );

  // The three buttons, as they look in a fight.
  const bar = h('div.lo-bar');
  ([0, 1, 2] as const).forEach((k) => {
    const pk = l.picks[k];
    bar.append(
      h(
        `button.lo-slot.el-${lookOf(pk.skill, pk.variant, glyphTurns(pk.skill, l.glyphs?.[k]))}${k === slot ? '.on' : ''}${k === 2 ? '.ult' : ''}`,
        { 'data-slot': String(k), 'aria-pressed': String(k === slot), onclick: () => ((slot = k), rerender()) },
        h('span.lo-ring', { html: ICONS[pk.skill] }),
        l.glyphs?.[k] ? h('span.lo-glyph', { html: ICONS[l.glyphs[k] as GlyphId], title: t(`glyph.${l.glyphs[k]}`) }) : '',
        h('span.lo-cap', {}, t(`tree.slot.${k}`)),
        h('b', {}, t(`skill.${pk.skill}`)),
        h('span.small', {}, pk.variant ? t(`fork.${pk.variant}`) : t('tree.variant.none')),
      ),
    );
  });
  wrap.append(bar);

  // The skills the slot can hold: the ones known, and the ones still to learn, dimmed.
  const pk = l.picks[slot];
  const known = knownSkills(hero, slot);
  const all: SkillId[] = [HEROES[cls].skills[slot], ...SKILL_TREES[cls].flatMap((n) => (n.branch === slot && n.effect.kind === 'learn' ? [n.effect.skill] : []))];
  if (all.length > 1)
    wrap.append(
      h('div.tc-cap', {}, t('carry.pick')),
      h(
        'div.lo-opts',
        {},
        ...all.map((id) =>
          h(
            `button.lo-opt.el-${SKILL_LOOK[id]}${id === pk.skill ? '.on' : ''}${known.includes(id) ? '' : '.dim'}`,
            { 'data-skill': id, disabled: over || !known.includes(id), onclick: () => void save(withPick(slot, id, null)) },
            h('span.pvar-ico', { html: ICONS[id] }),
            h('b', {}, t(`skill.${id}`)),
            known.includes(id) ? '' : h('span.small', {}, id === tomeSkill(hero, slot) ? t('tree.tomePages', { n: hero.tomePages?.[slot] ?? 0, m: TOMES.pages }) : t('carry.notLearned')),
          ),
        ),
      ),
    );

  // The ways to carry it, side by side: what each changes and what it's good for.
  const alone = skillAt(cls, hero.level, slot, null, pk.skill);
  const have = knownVariants(hero, pk.skill);
  const card = (v: ForkId | null): HTMLElement => {
    const on = v === pk.variant;
    const learned = v === null || have.includes(v);
    const sk = skillAt(cls, hero.level, slot, v, pk.skill);
    const diff = v ? diffOf(alone, sk) : [];
    return h(
      `button.lo-way.el-${lookOf(pk.skill, v)}${on ? '.on' : ''}${learned ? '' : '.no'}`,
      { 'data-variant': v ?? 'none', 'aria-pressed': String(on), disabled: over || !learned || on, onclick: () => void save(withPick(slot, pk.skill, v)) },
      h('span.pvar-ico', { html: v ? FORK_ICON[v] : ICONS[pk.skill] }),
      h(
        'span.pvar-txt',
        {},
        h('b', {}, v ? t(`fork.${v}`) : t('tree.variant.none'), on ? h('span.lo-use', {}, ` · ${t('carry.inUse')}`) : ''),
        h('span', {}, v ? t(`fork.${v}.desc`) : t('tree.variant.noneDesc')),
        diff.length ? h('span.pvar-diff', {}, ...diff) : '',
        v ? h('span.pvar-when', {}, learned ? t('tree.when', { w: t(`fork.${v}.when`) }) : t('carry.notLearned')) : '',
      ),
    );
  };
  const vs = variantsOf(cls, pk.skill);
  if (vs.length) wrap.append(h('div.tc-cap', {}, t('carry.variant', { s: t(`skill.${pk.skill}`) })), h('div.lo-ways', {}, card(null), ...vs.map(card)));

  // The unique piece whose power is this button's (docs/UNICAS.md §3): its two forms, like the variants, and its final one.
  for (const u of uniquesOnButton(hero, slot)) wrap.append(formPicker(hero, u, l, (next) => void save(next), over));

  // The glyph carved on this button (docs/GLIFOS.md): its slot opens with the level; each glyph goes on one button.
  if (hero.level >= GLYPH_SLOT_LEVELS[0]) {
    wrap.append(h('div.tc-cap', {}, t('carry.glyph', { s: t(`skill.${pk.skill}`) })));
    if (slot >= glyphSlotsOpen(hero.level)) wrap.append(h('p.small.lo-locked', { 'data-test': 'glyph-locked' }, t('glyph.locked', { n: GLYPH_SLOT_LEVELS[slot] })));
    else {
      const carved = l.glyphs?.[slot] ?? null;
      const plain = skillAt(cls, hero.level, slot, pk.variant, pk.skill);
      const glyphCard = (g: GlyphId | null): HTMLElement => {
        const on = g === carved;
        const owned = g === null || glyphOwned(hero, g);
        const misfit = g ? GLYPHS[g].misfit(pk.skill) : null;
        const elsewhere = g !== null && !on && !!l.glyphs?.some((x, i) => x === g && i !== slot);
        const ok = owned && !misfit && !elsewhere;
        const diff = g && !misfit ? diffOf(plain, skillAt(cls, hero.level, slot, pk.variant, pk.skill, g)) : [];
        const frags = g ? (hero.glyphFrags?.[g] ?? 0) : 0;
        return h(
          `button.lo-way.lo-gly${on ? '.on' : ''}${ok ? '' : '.no'}`,
          { 'data-glyph': g ?? 'none', 'aria-pressed': String(on), disabled: over || !ok || on, onclick: () => void save(withGlyph(slot, g)) },
          h('span.pvar-ico', { html: ICONS[g ?? 'glyph'] }),
          h(
            'span.pvar-txt',
            {},
            h('b', {}, g ? t(`glyph.${g}`) : t('glyph.none'), on ? h('span.lo-use', {}, ` · ${t('carry.inUse')}`) : ''),
            h('span', {}, g ? t(`glyph.${g}.desc`) : t('glyph.none.desc')),
            diff.length ? h('span.pvar-diff', {}, ...diff) : '',
            g && !owned ? h('span.pvar-when', {}, h('span.lo-pips', {}, ...Array.from({ length: GLYPH.fragments }, (_, i) => h(`i${i < frags ? '.on' : ''}`))), ` ${t('glyph.frags', { n: frags, m: GLYPH.fragments })} · ${t(`glyph.where.${GLYPHS[g].source}`)}`) : '',
            g && misfit ? h('span.pvar-when', {}, t(`glyph.no.${misfit}`)) : '',
            g && owned && !misfit && elsewhere ? h('span.pvar-when', {}, t('glyph.inOther')) : '',
          ),
        );
      };
      // The ones that fit first; the rest below, saying why not.
      const order = [...GLYPH_IDS].sort((a, b) => Number(glyphFits(pk.skill, b)) - Number(glyphFits(pk.skill, a)));
      wrap.append(h('div.lo-ways', { 'data-test': 'glyphs' }, glyphCard(null), ...order.map(glyphCard)));
    }
  }

  // The pieces whose power has no button (the frost mantle, the chapel lamp, the watchtower bow): their forms go by the passive.
  for (const u of uniquesOnButton(hero, null)) wrap.append(formPicker(hero, u, l, (next) => void save(next), over));

  // A paladin's aura goes with the preparation.
  if (cls === 'paladin') {
    const aura = l.aura ?? 'devotion';
    wrap.append(
      h('div.tc-cap', {}, t('aura.title')),
      h(
        'div.aura-row',
        { role: 'radiogroup', 'aria-label': t('aura.title') },
        ...PALADIN_AURAS.map((a) =>
          h(
            `button.btn.small.aura${a === aura ? '.on' : '.ghost'}`,
            { role: 'radio', 'aria-checked': String(a === aura), 'data-aura': a, disabled: over, onclick: () => (a === aura ? undefined : void save({ ...l, aura: a })) },
            t(`aura.${a}`),
          ),
        ),
      ),
      h('p.small.aura-desc', {}, t(`aura.${aura}.desc`)),
    );
  }
  const combos = combosWith(s, p, l, mode ?? 'wave');
  if (combos.length) wrap.append(h('div.tc-cap', {}, t('carry.combos', { m: t(`carry.mode.${mode ?? 'wave'}`) })), h('ul.lo-combos', {}, ...combos));
  wrap.append(h('p.small.pcarry', {}, t('carry.ghost')));
  return wrap;
};

/** What a variant or glyph changes from the skill without it: each number before and after, better in green. */
const diffOf = (was: SkillDef, now: SkillDef): HTMLElement[] =>
  (['damage', 'amount', 'duration', 'radius', 'castRange', 'stun', 'cooldown'] as const)
    .filter((k) => now[k] !== was[k])
    .map((k) => {
      const f = (n: number): string => (k === 'cooldown' ? `${Math.round((n * diffStats.cooldownBp) / 120000)} s` : String(k === 'radius' || k === 'castRange' ? n / 1000 : k === 'duration' || k === 'stun' ? Math.round(n / 12) : n));
      const label = k === 'castRange' ? t('tree.f.reach') : k === 'amount' ? t(`tree.amount.${was.id}`) : t(`tree.f.${k}`);
      return h('span', {}, `${label} `, h('s', {}, f(was[k])), ' → ', h(now[k] > was[k] === (k !== 'cooldown') ? 'b.up' : 'b.down', {}, f(now[k])));
    });
/** The hero's own recharge, for the seconds a diff shows (set when the editor draws). */
let diffStats = { cooldownBp: 10000 };

/** The two looks that set off each reaction. */
const REACTION_PAIRS: [string, string, string][] = [
  ['frost', 'bolt', 'shatter'],
  ['fire', 'poison', 'combust'],
];

/**
 * The reactions this preparation sets off with what the others carry to that kind of fight ("Shatter with
 * Tomás: your Blizzard and his Chain lightning"), so friends can plan without meeting.
 */
const combosWith = (s: CampaignState, p: PlayerState, l: Loadout, mode: CarryMode): HTMLElement[] => {
  const mine = l.picks.map((k, i) => ({ skill: k.skill, look: lookOf(k.skill, k.variant, glyphTurns(k.skill, l.glyphs?.[i])) }));
  const out: HTMLElement[] = [];
  for (const q of s.players) {
    if (q.id === p.id || q.left) continue;
    const c = carried(q.hero, mode);
    const theirs = c.skills.map((k, i) => ({ skill: k, look: lookOf(k, c.variants[i] ?? null, glyphTurns(k, c.glyphs?.[i])) }));
    for (const [a, b, r] of REACTION_PAIRS) {
      const m = mine.find((x) => x.look === a || x.look === b);
      const o = m && theirs.find((x) => x.look === (m.look === a ? b : a));
      if (!m || !o) continue;
      out.push(h(`li.el-${m.look}`, {}, h('b', {}, t(`react.${r}.with`, { who: q.displayName })), ' ', t('react.pair', { mine: t(`skill.${m.skill}`), theirs: t(`skill.${o.skill}`) })));
      if (out.length >= 3) return out;
    }
  }
  return out;
};
